// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { z } from 'zod';
import { UrlPatternStringSchema } from '../backend_config.types';
import type { JsonValue } from '../common.types';
import {
    IpxRbsValueTransformV1Schema,
    normalizeStoredIpxRbsValueTransformNumbers,
} from '../rbs-value-transform';
import {
    IpxRbsWorkflowBindingDirection,
    IpxRbsWorkflowBindingValueKind,
    IpxRbsWorkflowClass,
    IpxRbsWorkflowConstantBindingKey,
    IpxRbsWorkflowElementBindingListStorageVersion,
    RpaBuilderConfigurationRepairScopeKind,
    RpaBuilderConfigurationValidationIssueKind,
    RpaBuilderConfigurationValidationStatus,
    RpaBuilderInvalidIpsWorkflowInstanceReason,
    IpxRbsWorkflowWebsiteScopeKind,
    type IpxRbsWorkflowBindingDefinition,
    type IpxRbsWorkflowCompositeRuntimeProjection,
    type IpxRbsWorkflowEditorPolicy,
    type IpxRbsWorkflowElementBinding,
    type IpxRbsWorkflowElementBindingListV1,
    type IpxRbsWorkflowElementBindingListV2,
    type IpxRbsWorkflowElementBindingListV3,
    type IpxRbsWorkflowElementBindingV1,
    type IpxRbsWorkflowInstance,
    type IpxRbsWorkflowInstanceList,
    type IpxRbsWorkflowInstanceRuntimeProjection,
    type IpxRbsWorkflowRuntimeBootstrapRequest,
    type IpxRbsWorkflowRuntimeBootstrapResponse,
    type IpxRbsWorkflowRuntimeOpenerTarget,
    type IpxRbsWorkflowRuntimeProjection,
    type IpxRbsWorkflowWebsiteScope,
    type RpaBuilderConfigurationRepairScope,
    type RpaBuilderConfigurationValidationIssue,
    type RpaBuilderConfigurationValidationResult,
    type RpaBuilderFullConfigurationRepairScope,
    type RpaBuilderInvalidIpsWorkflowConfigurationIssue,
    type RpaBuilderInvalidIpsWorkflowInstanceProjection,
    type RpaBuilderInvalidLoginWorkflowConfigurationIssue,
    type RpaBuilderInvalidConfigurationValidationResult,
    type RpaBuilderInvalidWebsiteMapConfigurationIssue,
    type RpaBuilderIpsWorkflowInstanceProjection,
    type RpaBuilderNoConfigurationRepairScope,
    type RpaBuilderValidConfigurationValidationResult,
    type RpaBuilderWorkflowConfigurationRepairScope,
    type StoredIpxRbsWorkflowElementBindingList,
} from '../rbs-ips-workflow.types';
import { JsonValueSchema, NonEmptyStringSchema } from './common.schemas';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../stored-json-number';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';
import {
    IpxDomRpaWorkflowStepListSchema,
    IpxWebResourceElementKindSchema,
    IpxWebResourceModelDocumentSchema,
    RpaDomElementActionSchema,
} from './samba-rpa-workflow.schemas';
import {
    IpxCredentialRuntimeRoleScopeSchema,
    RpaBuilderInvalidLoginWorkflowDefinitionProjectionSchema,
    RpaBuilderInvalidWebResourceModelProjectionSchema,
} from './secret.schemas';
import { IpxRbsWorkflowWebsiteScopeSchema } from './rbs-website-scope.schemas';

export const IpxRbsWorkflowUuidSchema: z.ZodType<string> = z.string().uuid();

export const IpxRbsWorkflowRevisionSchema: z.ZodType<string> = NonEmptyStringSchema;

export const IpxRbsWorkflowOrderSchema: z.ZodType<number> = z.number().int().min(0);

export const IpxRbsWorkflowClassSchema: z.ZodType<IpxRbsWorkflowClass> =
    z.enum(IpxRbsWorkflowClass);

export const IpxRbsWorkflowWebsiteScopeKindSchema: z.ZodType<IpxRbsWorkflowWebsiteScopeKind> =
    z.enum(IpxRbsWorkflowWebsiteScopeKind);

export const IpxRbsWorkflowBindingDirectionSchema: z.ZodType<IpxRbsWorkflowBindingDirection> =
    z.enum(IpxRbsWorkflowBindingDirection);

export const IpxRbsWorkflowBindingValueKindSchema: z.ZodType<IpxRbsWorkflowBindingValueKind> =
    z.enum(IpxRbsWorkflowBindingValueKind);

export const IpxRbsWorkflowBindingDefinitionSchema: z.ZodType<IpxRbsWorkflowBindingDefinition> = z
    .object({
        binding_key: NonEmptyStringSchema,
        display_name: NonEmptyStringSchema,
        direction: IpxRbsWorkflowBindingDirectionSchema,
        value_kind: IpxRbsWorkflowBindingValueKindSchema,
        required: z.boolean(),
        allowed_target_kinds: z.array(IpxWebResourceElementKindSchema).min(1),
        allowed_actions: z.array(RpaDomElementActionSchema).min(1),
    })
    .strict();

export const IpxRbsWorkflowElementBindingV1Schema: z.ZodType<IpxRbsWorkflowElementBindingV1> = z
    .object({
        binding_key: NonEmptyStringSchema,
        element_key: NonEmptyStringSchema,
        value_path: NonEmptyStringSchema.optional(),
    })
    .strict();

export const IpxRbsWorkflowElementBindingSchema: z.ZodType<IpxRbsWorkflowElementBinding> = z
    .object({
        binding_key: NonEmptyStringSchema,
        element_key: NonEmptyStringSchema,
        value_path: NonEmptyStringSchema.optional(),
        value_transform: IpxRbsValueTransformV1Schema.optional(),
        constant_value: z.string().min(1).max(1_024).optional(),
    })
    .strict()
    .superRefine((binding, ctx) => {
        if ((binding.binding_key === IpxRbsWorkflowConstantBindingKey) !== (binding.constant_value !== undefined)) {
            ctx.addIssue({
                code: 'custom',
                message: 'A constant binding, and only a constant binding, carries constant_value',
                path: ['constant_value'],
            });
        }
    });

export const IpxRbsWorkflowElementBindingListSchema: z.ZodType<
    readonly IpxRbsWorkflowElementBinding[]
> = z.array(IpxRbsWorkflowElementBindingSchema);

export const IpxRbsWorkflowElementBindingListStorageVersionSchema: z.ZodType<IpxRbsWorkflowElementBindingListStorageVersion> =
    z.union([z.literal(1), z.literal(2), z.literal(3)]);

const IpxRbsWorkflowElementBindingListStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'ipx-rbs-workflow-element-binding-list.binding_list_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(IpxRbsWorkflowElementBindingListStorageVersion),
    },
});

export const IpxRbsWorkflowElementBindingListV1Schema: z.ZodType<IpxRbsWorkflowElementBindingListV1> =
    z.array(IpxRbsWorkflowElementBindingV1Schema);

export const IpxRbsWorkflowElementBindingListV2Schema: z.ZodType<IpxRbsWorkflowElementBindingListV2> =
    z
        .object({
            binding_list_version: z.literal(2),
            bindings: z.array(IpxRbsWorkflowElementBindingV1Schema),
        })
        .strict();

export const IpxRbsWorkflowElementBindingListV3Schema: z.ZodType<IpxRbsWorkflowElementBindingListV3> =
    z
        .object({
            binding_list_version: z.literal(3),
            bindings: z.array(IpxRbsWorkflowElementBindingSchema),
        })
        .strict();

export const StoredIpxRbsWorkflowElementBindingListV2Schema: z.ZodType<IpxRbsWorkflowElementBindingListV2> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: IpxRbsWorkflowElementBindingListStorageVersionSafeIntegerDomain,
                fields: ['binding_list_version'],
            }),
        IpxRbsWorkflowElementBindingListV2Schema
    );

const StoredBindingListObjectSchema = z.object({ bindings: z.array(z.unknown()) }).passthrough();
const StoredBindingObjectSchema = z.object({ value_transform: z.unknown() }).passthrough();

function normalizeStoredIpxRbsWorkflowElementBindingListV3Numbers(value: unknown): unknown {
    const versioned = normalizeStoredJsonSafeIntegerObjectFields(value, {
        safeIntegerDomain: IpxRbsWorkflowElementBindingListStorageVersionSafeIntegerDomain,
        fields: ['binding_list_version'],
    });
    const bindingList = StoredBindingListObjectSchema.safeParse(versioned);
    if (!bindingList.success) {
        return versioned;
    }
    return {
        ...bindingList.data,
        bindings: bindingList.data.bindings.map((binding: unknown): unknown => {
            const transformed = StoredBindingObjectSchema.safeParse(binding);
            return transformed.success && transformed.data.value_transform !== undefined
                ? {
                      ...transformed.data,
                      value_transform: normalizeStoredIpxRbsValueTransformNumbers(
                          transformed.data.value_transform
                      ),
                  }
                : binding;
        }),
    };
}

export const StoredIpxRbsWorkflowElementBindingListV3Schema: z.ZodType<IpxRbsWorkflowElementBindingListV3> =
    z.preprocess(
        normalizeStoredIpxRbsWorkflowElementBindingListV3Numbers,
        IpxRbsWorkflowElementBindingListV3Schema
    );

export const StoredIpxRbsWorkflowElementBindingListSchema: z.ZodType<StoredIpxRbsWorkflowElementBindingList> =
    z.union([
        StoredIpxRbsWorkflowElementBindingListV3Schema,
        StoredIpxRbsWorkflowElementBindingListV2Schema,
        IpxRbsWorkflowElementBindingListV1Schema,
    ]);

export function normalizeIpxRbsWorkflowElementBindingListV1(
    bindingList: IpxRbsWorkflowElementBindingListV1
): readonly IpxRbsWorkflowElementBinding[] {
    return bindingList;
}

export function normalizeIpxRbsWorkflowElementBindingListV2(
    bindingList: IpxRbsWorkflowElementBindingListV2
): readonly IpxRbsWorkflowElementBinding[] {
    return bindingList.bindings;
}

export function normalizeIpxRbsWorkflowElementBindingListV3(
    bindingList: IpxRbsWorkflowElementBindingListV3
): readonly IpxRbsWorkflowElementBinding[] {
    return bindingList.bindings;
}

export const IpxRbsWorkflowElementBindingListStorageContract = defineStoredJsonContract({
    contractName: 'IpxRbsWorkflowElementBinding[]',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'IpxRbsWorkflowElementBindingListV1Schema',
            schema: IpxRbsWorkflowElementBindingListV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['binding_list_version'],
                },
            ],
            normalize: normalizeIpxRbsWorkflowElementBindingListV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredIpxRbsWorkflowElementBindingListV2Schema',
            schema: StoredIpxRbsWorkflowElementBindingListV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['binding_list_version'],
                    markerValue: 2,
                },
            ],
            normalize: normalizeIpxRbsWorkflowElementBindingListV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v3: {
            schemaName: 'StoredIpxRbsWorkflowElementBindingListV3Schema',
            schema: StoredIpxRbsWorkflowElementBindingListV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['binding_list_version'],
                    markerValue: 3,
                },
            ],
            normalize: normalizeIpxRbsWorkflowElementBindingListV3,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredIpxRbsWorkflowElementBindingListParser: z.ZodType<
    readonly IpxRbsWorkflowElementBinding[]
> = buildStoredJsonValueParser(IpxRbsWorkflowElementBindingListStorageContract);

export function serializeIpxRbsWorkflowElementBindingListV3(
    bindings: readonly IpxRbsWorkflowElementBinding[]
): IpxRbsWorkflowElementBindingListV3 {
    return {
        binding_list_version: 3,
        bindings: IpxRbsWorkflowElementBindingListSchema.parse(bindings),
    };
}

function addSubsetIssues<T extends string>(input: {
    readonly values: readonly T[];
    readonly allowedValues: ReadonlySet<T>;
    readonly path: readonly (string | number)[];
    readonly ctx: z.RefinementCtx;
    readonly message: string;
}): void {
    for (const [valueIndex, value] of input.values.entries()) {
        if (!input.allowedValues.has(value)) {
            input.ctx.addIssue({
                code: 'custom',
                message: input.message,
                path: [...input.path, valueIndex],
            });
        }
    }
}

export const IpxRbsWorkflowEditorPolicySchema: z.ZodType<IpxRbsWorkflowEditorPolicy> = z
    .object({
        workflow_class: IpxRbsWorkflowClassSchema,
        allowed_target_kinds: z.array(IpxWebResourceElementKindSchema).min(1),
        allowed_actions: z.array(RpaDomElementActionSchema).min(1),
        input_bindings: z.array(IpxRbsWorkflowBindingDefinitionSchema),
        output_bindings: z.array(IpxRbsWorkflowBindingDefinitionSchema),
    })
    .strict()
    .superRefine((policy, ctx) => {
        const allowedTargetKinds = new Set(policy.allowed_target_kinds);
        const allowedActions = new Set(policy.allowed_actions);
        const bindings = [
            ...policy.input_bindings.map((binding, bindingIndex) => ({
                binding,
                path: ['input_bindings', bindingIndex],
            })),
            ...policy.output_bindings.map((binding, bindingIndex) => ({
                binding,
                path: ['output_bindings', bindingIndex],
            })),
        ];
        const seenBindingKeys = new Set<string>();
        for (const { binding, path } of bindings) {
            if (seenBindingKeys.has(binding.binding_key)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Workflow binding keys must be unique',
                    path: [...path, 'binding_key'],
                });
            }
            seenBindingKeys.add(binding.binding_key);
            addSubsetIssues({
                values: binding.allowed_target_kinds,
                allowedValues: allowedTargetKinds,
                path: [...path, 'allowed_target_kinds'],
                ctx,
                message: 'Binding target kind must be allowed by the policy',
            });
            addSubsetIssues({
                values: binding.allowed_actions,
                allowedValues: allowedActions,
                path: [...path, 'allowed_actions'],
                ctx,
                message: 'Binding action must be allowed by the policy',
            });
        }
    });

export const IpxRbsWorkflowClassEditorPolicyMapSchema: z.ZodType<
    Record<IpxRbsWorkflowClass, IpxRbsWorkflowEditorPolicy>
> = z.record(IpxRbsWorkflowClassSchema, IpxRbsWorkflowEditorPolicySchema);

export const IpxRbsWorkflowDefaultEditorPoliciesSchema: z.ZodType<
    Record<IpxRbsWorkflowClass, IpxRbsWorkflowEditorPolicy>
> = IpxRbsWorkflowClassEditorPolicyMapSchema.superRefine((policies, ctx) => {
    for (const workflowClass of Object.values(IpxRbsWorkflowClass)) {
        const policy = policies[workflowClass];
        if (policy === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Workflow class is missing an editor policy',
                path: [workflowClass],
            });
            continue;
        }
        if (policy.workflow_class !== workflowClass) {
            ctx.addIssue({
                code: 'custom',
                message: 'Editor policy key must match workflow class',
                path: [workflowClass, 'workflow_class'],
            });
        }
    }
});

export const IpxRbsWorkflowClassMetadataSchema: z.ZodType<Record<string, JsonValue>> = z.record(
    z.string(),
    JsonValueSchema
);

export const IpxRbsWorkflowInstanceSchema: z.ZodType<IpxRbsWorkflowInstance> = z
    .object({
        workflow_instance_uuid: IpxRbsWorkflowUuidSchema,
        website_scope: IpxRbsWorkflowWebsiteScopeSchema,
        workflow_class: IpxRbsWorkflowClassSchema,
        order: IpxRbsWorkflowOrderSchema,
        name: NonEmptyStringSchema,
        enabled: z.boolean(),
        ipx_web_resource_model_uuid: z.string().uuid(),
        ipx_web_resource_model_revision: IpxRbsWorkflowRevisionSchema,
        workflow: IpxDomRpaWorkflowStepListSchema,
        input_bindings: z.array(IpxRbsWorkflowElementBindingSchema),
        output_bindings: z.array(IpxRbsWorkflowElementBindingSchema),
        class_metadata: IpxRbsWorkflowClassMetadataSchema,
    })
    .strict();

function formatWebsiteScopeKey(scope: IpxRbsWorkflowWebsiteScope): string {
    switch (scope.kind) {
        case IpxRbsWorkflowWebsiteScopeKind.ALLOWED_WEBSITE_TEMPLATE:
            return [scope.kind, scope.allowed_website_template_uuid].join(':');
        case IpxRbsWorkflowWebsiteScopeKind.ORGANIZATION_ALLOWED_WEBSITE:
            return [scope.kind, scope.organization_allowed_website_uuid].join(':');
    }
}

export const IpxRbsWorkflowInstanceListSchema: z.ZodType<IpxRbsWorkflowInstanceList> = z
    .object({
        workflow_instances: z.array(IpxRbsWorkflowInstanceSchema),
    })
    .strict()
    .superRefine((list, ctx) => {
        const orderOwners = new Map<string, number>();
        for (const [instanceIndex, instance] of list.workflow_instances.entries()) {
            const orderKey = [
                formatWebsiteScopeKey(instance.website_scope),
                instance.workflow_class,
                String(instance.order),
            ].join('\u0000');
            const existingIndex = orderOwners.get(orderKey);
            if (existingIndex !== undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Workflow instance order must be unique per website and class',
                    path: ['workflow_instances', instanceIndex, 'order'],
                });
                ctx.addIssue({
                    code: 'custom',
                    message: 'Workflow instance order must be unique per website and class',
                    path: ['workflow_instances', existingIndex, 'order'],
                });
                continue;
            }
            orderOwners.set(orderKey, instanceIndex);
        }
    });

export const IpxRbsWorkflowInstanceRuntimeProjectionSchema: z.ZodType<IpxRbsWorkflowInstanceRuntimeProjection> =
    z
        .object({
            workflow_instance_uuid: IpxRbsWorkflowUuidSchema,
            order: IpxRbsWorkflowOrderSchema,
            name: NonEmptyStringSchema,
            workflow: IpxDomRpaWorkflowStepListSchema,
            input_bindings: z.array(IpxRbsWorkflowElementBindingSchema),
            output_bindings: z.array(IpxRbsWorkflowElementBindingSchema),
            class_metadata: IpxRbsWorkflowClassMetadataSchema,
        })
        .strict();

export const IpxRbsWorkflowCompositeRuntimeProjectionSchema: z.ZodType<IpxRbsWorkflowCompositeRuntimeProjection> =
    z
        .object({
            workflow_class: IpxRbsWorkflowClassSchema,
            workflow_id: NonEmptyStringSchema,
            page_url_patterns: z.array(UrlPatternStringSchema).min(1),
            editor_policy: IpxRbsWorkflowEditorPolicySchema,
            instances: z.array(IpxRbsWorkflowInstanceRuntimeProjectionSchema).min(1),
        })
        .strict()
        .superRefine((projection, ctx) => {
            if (projection.editor_policy.workflow_class !== projection.workflow_class) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Composite editor policy must match workflow class',
                    path: ['editor_policy', 'workflow_class'],
                });
            }
            const orderOwners = new Map<number, number>();
            for (const [instanceIndex, instance] of projection.instances.entries()) {
                const existingIndex = orderOwners.get(instance.order);
                if (existingIndex !== undefined) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Runtime projection order must be unique per composite',
                        path: ['instances', instanceIndex, 'order'],
                    });
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Runtime projection order must be unique per composite',
                        path: ['instances', existingIndex, 'order'],
                    });
                    continue;
                }
                orderOwners.set(instance.order, instanceIndex);
            }
        });

export const IpxRbsWorkflowRuntimeProjectionSchema: z.ZodType<IpxRbsWorkflowRuntimeProjection> = z
    .object({
        projection_id: NonEmptyStringSchema,
        website_scope: IpxRbsWorkflowWebsiteScopeSchema,
        allowed_website_owner: IpxRbsWorkflowWebsiteScopeSchema,
        allowed_website_url: z.string().url(),
        ipx_web_resource_model_uuid: z.string().uuid(),
        ipx_web_resource_model_revision: IpxRbsWorkflowRevisionSchema,
        web_resource_model: IpxWebResourceModelDocumentSchema,
        composites: z.array(IpxRbsWorkflowCompositeRuntimeProjectionSchema),
    })
    .strict();

export const IpxRbsWorkflowRuntimeBootstrapRequestSchema: z.ZodType<IpxRbsWorkflowRuntimeBootstrapRequest> =
    z
        .object({
            role_scope: IpxCredentialRuntimeRoleScopeSchema,
            supported_value_transform_versions: z.array(z.literal(1)).optional(),
        })
        .strict();

export const IpxRbsWorkflowRuntimeOpenerTargetSchema: z.ZodType<IpxRbsWorkflowRuntimeOpenerTarget> =
    z
        .object({
            name: NonEmptyStringSchema,
            allowed_website_owner: IpxRbsWorkflowWebsiteScopeSchema,
            allowed_website_url: z.string().url(),
            allowed_url_patterns: z.array(UrlPatternStringSchema).min(1),
        })
        .strict();

export const IpxRbsWorkflowRuntimeBootstrapResponseSchema: z.ZodType<IpxRbsWorkflowRuntimeBootstrapResponse> =
    z
        .object({
            role_scope: IpxCredentialRuntimeRoleScopeSchema,
            opener_targets: z.array(IpxRbsWorkflowRuntimeOpenerTargetSchema),
            projections: z.array(IpxRbsWorkflowRuntimeProjectionSchema),
        })
        .strict();

export const RpaBuilderInvalidIpsWorkflowInstanceReasonSchema: z.ZodType<RpaBuilderInvalidIpsWorkflowInstanceReason> =
    z.enum(RpaBuilderInvalidIpsWorkflowInstanceReason);

export const RpaBuilderInvalidIpsWorkflowInstanceProjectionSchema: z.ZodType<RpaBuilderInvalidIpsWorkflowInstanceProjection> =
    z
        .object({
            invalid_reason: z.enum(RpaBuilderInvalidIpsWorkflowInstanceReason),
            workflow_instance_uuid: IpxRbsWorkflowUuidSchema,
            website_scope: IpxRbsWorkflowWebsiteScopeSchema,
            workflow_class: z.union([IpxRbsWorkflowClassSchema, NonEmptyStringSchema]),
            order: IpxRbsWorkflowOrderSchema,
            name: NonEmptyStringSchema,
            enabled: z.boolean(),
            ipx_web_resource_model_uuid: z.string().uuid().optional(),
            ipx_web_resource_model_revision: IpxRbsWorkflowRevisionSchema.optional(),
            parse_errors: z.array(NonEmptyStringSchema),
        })
        .strict();

export const RpaBuilderIpsWorkflowInstanceProjectionSchema: z.ZodType<RpaBuilderIpsWorkflowInstanceProjection> =
    z.union([IpxRbsWorkflowInstanceSchema, RpaBuilderInvalidIpsWorkflowInstanceProjectionSchema]);

export const RpaBuilderConfigurationValidationStatusSchema: z.ZodType<RpaBuilderConfigurationValidationStatus> =
    z.enum(RpaBuilderConfigurationValidationStatus);

export const RpaBuilderConfigurationValidationIssueKindSchema: z.ZodType<RpaBuilderConfigurationValidationIssueKind> =
    z.enum(RpaBuilderConfigurationValidationIssueKind);

export const RpaBuilderInvalidWebsiteMapConfigurationIssueSchema = z
    .object({
        kind: z.literal(RpaBuilderConfigurationValidationIssueKind.INVALID_WEBSITE_MAP),
        web_resource_model: RpaBuilderInvalidWebResourceModelProjectionSchema,
    })
    .strict() satisfies z.ZodType<RpaBuilderInvalidWebsiteMapConfigurationIssue>;

export const RpaBuilderInvalidLoginWorkflowConfigurationIssueSchema = z
    .object({
        kind: z.literal(RpaBuilderConfigurationValidationIssueKind.INVALID_LOGIN_WORKFLOW),
        credential_definition: RpaBuilderInvalidLoginWorkflowDefinitionProjectionSchema,
    })
    .strict() satisfies z.ZodType<RpaBuilderInvalidLoginWorkflowConfigurationIssue>;

export const RpaBuilderInvalidIpsWorkflowConfigurationIssueSchema = z
    .object({
        kind: z.literal(RpaBuilderConfigurationValidationIssueKind.INVALID_IPS_WORKFLOW),
        workflow_instance: RpaBuilderInvalidIpsWorkflowInstanceProjectionSchema,
    })
    .strict() satisfies z.ZodType<RpaBuilderInvalidIpsWorkflowConfigurationIssue>;

export const RpaBuilderConfigurationValidationIssueSchema = z.discriminatedUnion('kind', [
    RpaBuilderInvalidWebsiteMapConfigurationIssueSchema,
    RpaBuilderInvalidLoginWorkflowConfigurationIssueSchema,
    RpaBuilderInvalidIpsWorkflowConfigurationIssueSchema,
]) satisfies z.ZodType<RpaBuilderConfigurationValidationIssue>;

export const RpaBuilderConfigurationRepairScopeKindSchema: z.ZodType<RpaBuilderConfigurationRepairScopeKind> =
    z.enum(RpaBuilderConfigurationRepairScopeKind);

export const RpaBuilderNoConfigurationRepairScopeSchema = z
    .object({
        kind: z.literal(RpaBuilderConfigurationRepairScopeKind.NONE),
    })
    .strict() satisfies z.ZodType<RpaBuilderNoConfigurationRepairScope>;

export const RpaBuilderFullConfigurationRepairScopeSchema = z
    .object({
        kind: z.literal(RpaBuilderConfigurationRepairScopeKind.FULL_CONFIGURATION),
        invalid_website_map: RpaBuilderInvalidWebResourceModelProjectionSchema,
    })
    .strict() satisfies z.ZodType<RpaBuilderFullConfigurationRepairScope>;

export const RpaBuilderWorkflowConfigurationRepairScopeSchema = z
    .object({
        kind: z.literal(RpaBuilderConfigurationRepairScopeKind.WORKFLOWS),
        invalid_login_workflows: z.array(RpaBuilderInvalidLoginWorkflowDefinitionProjectionSchema),
        invalid_ips_workflows: z.array(RpaBuilderInvalidIpsWorkflowInstanceProjectionSchema),
    })
    .strict() satisfies z.ZodType<RpaBuilderWorkflowConfigurationRepairScope>;

export const RpaBuilderConfigurationRepairScopeSchema = z.discriminatedUnion('kind', [
    RpaBuilderNoConfigurationRepairScopeSchema,
    RpaBuilderFullConfigurationRepairScopeSchema,
    RpaBuilderWorkflowConfigurationRepairScopeSchema,
]) satisfies z.ZodType<RpaBuilderConfigurationRepairScope>;

export const RpaBuilderValidConfigurationValidationResultSchema = z
    .object({
        status: z.literal(RpaBuilderConfigurationValidationStatus.VALID),
        issues: z.array(RpaBuilderConfigurationValidationIssueSchema).length(0),
        repair_scope: RpaBuilderNoConfigurationRepairScopeSchema,
    })
    .strict() satisfies z.ZodType<RpaBuilderValidConfigurationValidationResult>;

export const RpaBuilderInvalidConfigurationValidationResultSchema = z
    .object({
        status: z.literal(RpaBuilderConfigurationValidationStatus.INVALID),
        issues: z.array(RpaBuilderConfigurationValidationIssueSchema).min(1),
        repair_scope: z.discriminatedUnion('kind', [
            RpaBuilderFullConfigurationRepairScopeSchema,
            RpaBuilderWorkflowConfigurationRepairScopeSchema,
        ]),
    })
    .strict() satisfies z.ZodType<RpaBuilderInvalidConfigurationValidationResult>;

export const RpaBuilderConfigurationValidationResultSchema = z.discriminatedUnion('status', [
    RpaBuilderValidConfigurationValidationResultSchema,
    RpaBuilderInvalidConfigurationValidationResultSchema,
]) satisfies z.ZodType<RpaBuilderConfigurationValidationResult>;
